/**
 * Exportación PDF/Excel reutilizable — cubre el hueco encontrado en la auditoría de
 * V2 (2026-09-28): NINGÚN listado tenía los botones "PDF"/"Excel"/"eMail" que V1
 * SIEMPRE tiene (ver p.ej. `IbuFront/src/components/views/Admin/Bancos/ListadoBancos.jsx`
 * → `PDFTabla()`/`excel()`, o `.../Admin/Cargos/ListadoCargos.jsx`).
 *
 * V1 usaba `jsPDF` + `doc.autoTable(...)` (plugin viejo que parchaba el prototipo) y
 * `js-export-excel`. V2 ya trae `jspdf`@3 + `jspdf-autotable`@5 + `xlsx`@0.18
 * instalados (`package.json`) pero NINGUNA dependencia nueva hace falta:
 * - `jspdf-autotable`@5 ya no parcha el prototipo de jsPDF -- se usa como función
 *   modular `autoTable(doc, opciones)` (ver `node_modules/jspdf-autotable/dist/index.d.ts`).
 * - `js-export-excel` NO está instalado en V2 -- se usa `xlsx` (SheetJS), que sí lo
 *   está, en su lugar (misma UX para el usuario: un botón "Excel" que descarga
 *   `.xlsx` del listado filtrado).
 *
 * El botón "eMail" de V1 arma el mismo PDF en base64 y lo manda a SendGrid: en V2 usar
 * `buildTablePdf` + `pdfADataBase64` + `services/api/mail.js` (ver su cabecera).
 *
 * @module utils/exportUtils
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

/**
 * Calcula el valor "plano" (string/number) de una columna para una fila, igual que
 * la tabla en pantalla: usa `col.render(row)` si existe (en toda la config real de
 * V2 -- `config/resources.js`, `resources.finanzas-cxc.jsx`, páginas bespoke -- los
 * `render` de columnas de datos devuelven strings/numbers, nunca JSX; el único que
 * devuelve JSX es la columna `__acciones`, que este módulo siempre excluye).
 */
function valorColumna(col, row) {
  const v = col.render ? col.render(row) : row?.[col.key];
  if (v === null || v === undefined) return '';
  return v;
}

/** Columnas exportables: quita `__acciones` y cualquier columna marcada `noExport`. */
function columnasExportables(columnas) {
  return (columnas || []).filter((c) => c.key !== '__acciones' && !c.noExport);
}

// ---------------------------------------------------------------------------
// Logos de PDF (igual que V1: Logo-Sistemify-PDF.png + logoInstitucion.png)
// ---------------------------------------------------------------------------
const LOGOS = { sistemify: 'Logo-Sistemify-PDF.png', institucion: 'logoInstitucion.png' };
let logosPromise = null;

function blobADataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

/** Precarga (una sola vez, cacheada) los logos de `/public` como dataURL. Nunca rechaza. */
export function cargarLogosPdf() {
  if (!logosPromise) {
    const base = import.meta.env.BASE_URL || '/';
    const cargar = async (archivo) => {
      try {
        const res = await fetch(`${base}${archivo}`);
        if (!res.ok) return null;
        return await blobADataUrl(await res.blob());
      } catch {
        return null;
      }
    };
    logosPromise = Promise.all([cargar(LOGOS.sistemify), cargar(LOGOS.institucion)]).then(([sistemify, institucion]) => {
      if (!sistemify && !institucion) logosPromise = null; // permite reintentar si falló todo
      return { sistemify, institucion };
    });
  }
  return logosPromise;
}

/**
 * Dibuja los logos (Sistemify + institución) en la página actual de un `jsPDF` propio.
 * Posiciones tomadas de V1 (mm):
 *   landscape: Sistemify (230,30,45,15), institución (140,15,25,25)
 *   portrait : Sistemify (150,10,33,10), institución (160,23,25,25)
 * Deja libre el área izquierda (título en x<=120) y la tabla debe empezar en y>=45
 * (landscape) / y>=52 (portrait).
 *
 * @param {import('jspdf').jsPDF} doc
 * @param {{orientation?: 'portrait'|'landscape'}} [opts] default: se deduce del doc.
 * @returns {Promise<import('jspdf').jsPDF>} el mismo doc (async: espera la precarga).
 */
export async function agregarLogosPdf(doc, opts = {}) {
  const { sistemify, institucion } = await cargarLogosPdf();
  const orientation =
    opts.orientation || (doc.internal.pageSize.getWidth() > doc.internal.pageSize.getHeight() ? 'landscape' : 'portrait');
  const land = orientation === 'landscape';
  try {
    if (sistemify) doc.addImage(sistemify, 'PNG', ...(land ? [230, 30, 45, 15] : [150, 10, 33, 10]));
    if (institucion) doc.addImage(institucion, 'PNG', ...(land ? [140, 15, 25, 25] : [160, 23, 25, 25]));
  } catch (e) {
    console.warn('No se pudieron agregar los logos al PDF', e);
  }
  return doc;
}

/** Base64 (sin prefijo data:) de un jsPDF, listo para el campo `att` del backend de correo. */
export function pdfADataBase64(doc) {
  const uri = doc.output('datauristring');
  return uri.slice(uri.indexOf('base64,') + 7);
}

/**
 * Construye (sin guardar) el PDF de un listado, con logos. Mismas opciones que
 * `exportTablePdf`. Retorna Promise<jsPDF> (async por la precarga de logos).
 */
export async function buildTablePdf(titulo, columnas, filas, opciones = {}) {
  const cols = columnasExportables(columnas);
  const orientation = opciones.orientation || 'landscape';
  const doc = new jsPDF({ orientation });
  await agregarLogosPdf(doc, { orientation });

  doc.setFontSize(14);
  doc.text(String(titulo || ''), 14, 25);
  // Debajo de los logos (V1: startY 45 landscape; portrait los logos llegan a y=48).
  let startY = orientation === 'landscape' ? 45 : 52;
  if (opciones.subtitulo) {
    doc.setFontSize(10);
    doc.text(String(opciones.subtitulo), 14, 32);
  }

  const body = (filas || []).map((row) => cols.map((c) => String(valorColumna(c, row))));
  // `footRows` (opcional): filas de pie arbitrarias (arreglos de strings, una por renglón,
  // p.ej. Saldo Anterior / Cargos / Abonos / Saldo Actual del estado de cuenta de banco).
  const foot = opciones.footRows
    ? opciones.footRows
    : opciones.totalCols
      ? [cols.map((c) => (opciones.totalCols[c.key] !== undefined ? String(opciones.totalCols[c.key]) : ''))]
      : undefined;

  autoTable(doc, {
    head: [cols.map((c) => c.label)],
    body,
    foot,
    startY,
    showFoot: foot ? 'lastPage' : undefined,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [30, 64, 175] },
    footStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold' },
  });
  return doc;
}

/**
 * Genera y descarga un PDF (jsPDF + autoTable, con logos) de un listado ya filtrado.
 * Firma sin cambios; ahora retorna Promise<void> (los llamadores actuales no esperan).
 *
 * @param {string} titulo Título mostrado arriba de la tabla y usado como nombre de archivo.
 * @param {Array<{key:string,label:string,render?:Function}>} columnas
 * @param {Array<Object>} filas
 * @param {Object} [opciones]
 * @param {string} [opciones.subtitulo] Línea secundaria (ej. rango de fechas).
 * @param {'portrait'|'landscape'} [opciones.orientation='landscape']
 * @param {string} [opciones.fileName] Nombre de archivo sin extensión (default: `titulo`).
 * @param {Object.<string,string|number>} [opciones.totalCols] Pie de totales por `key`
 *        de columna (ej. `{ importe: '$1,234.00' }`), igual que el `foot` de V1.
 * @param {string[][]} [opciones.footRows] Filas de pie arbitrarias (tiene prioridad sobre totalCols).
 */
export async function exportTablePdf(titulo, columnas, filas, opciones = {}) {
  const doc = await buildTablePdf(titulo, columnas, filas, opciones);
  const nombre = (opciones.fileName || titulo || 'listado').toString().replace(/[^a-z0-9-_]+/gi, '_');
  doc.save(`${nombre}.pdf`);
}

/**
 * Genera y descarga un Excel (.xlsx vía SheetJS) de un listado ya filtrado.
 *
 * @param {string} titulo Título usado como nombre de archivo si no se da `opciones.fileName`.
 * @param {Array<{key:string,label:string,render?:Function}>} columnas
 * @param {Array<Object>} filas
 * @param {Object} [opciones]
 * @param {string} [opciones.fileName] Nombre de archivo sin extensión.
 * @param {string} [opciones.sheetName='Datos']
 */
export function exportTableExcel(titulo, columnas, filas, opciones = {}) {
  const cols = columnasExportables(columnas);
  const data = (filas || []).map((row) => {
    const obj = {};
    cols.forEach((c) => {
      obj[c.label] = valorColumna(c, row);
    });
    return obj;
  });

  const hoja = XLSX.utils.json_to_sheet(data, { header: cols.map((c) => c.label) });
  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, hoja, (opciones.sheetName || 'Datos').slice(0, 31));

  const nombre = (opciones.fileName || titulo || 'listado').toString().replace(/[^a-z0-9-_]+/gi, '_');
  XLSX.writeFile(libro, `${nombre}.xlsx`);
}

export default { exportTablePdf, exportTableExcel, buildTablePdf, agregarLogosPdf, pdfADataBase64, cargarLogosPdf };
